// Utility functions for sessionStorage operations
import * as Sentry from '@sentry/nextjs';
export const setSessionStorageItem = (key, value) => {
  if (typeof window === 'undefined') {
    // Server-side rendering check
    return;
  }
  try {
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(key, serializedValue);
  } catch (error) {
    // Check if it's a quota exceeded error
    const isQuotaError = error instanceof DOMException && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');
    if (isQuotaError) {
      // Fallback to localStorage if sessionStorage is full
      try {
        const serializedValue = JSON.stringify(value);
        localStorage.setItem(key, serializedValue);
        Sentry.captureMessage('sessionStorage quota exceeded, falling back to localStorage', {
          tags: {
            operation: 'sessionStorage_set_fallback'
          },
          extra: {
            key
          }
        });
      } catch (localStorageError) {
        Sentry.captureException(localStorageError, {
          tags: {
            operation: 'localStorage_set_fallback'
          },
          extra: {
            key,
            originalError: error
          }
        });
      }
    } else {
      Sentry.captureException(error, {
        tags: {
          operation: 'sessionStorage_set'
        },
        extra: {
          key
        }
      });
    }
  }
};
export const getSessionStorageItem = key => {
  if (typeof window === 'undefined') {
    // Server-side rendering check
    return null;
  }
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      // Fallback to localStorage if not found in sessionStorage
      try {
        const localItem = localStorage.getItem(key);
        if (localItem === null) {
          return null;
        }
        return JSON.parse(localItem);
      } catch (localStorageError) {
        Sentry.captureException(localStorageError, {
          tags: {
            operation: 'localStorage_get_fallback'
          },
          extra: {
            key
          }
        });
        return null;
      }
    }
    return JSON.parse(item);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        operation: 'sessionStorage_get'
      },
      extra: {
        key
      }
    });
    return null;
  }
};
export const removeSessionStorageItem = key => {
  if (typeof window === 'undefined') {
    // Server-side rendering check
    return;
  }
  try {
    sessionStorage.removeItem(key);
    // Also remove from localStorage in case it was stored there as fallback
    localStorage.removeItem(key);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        operation: 'sessionStorage_remove'
      },
      extra: {
        key
      }
    });
  }
};
export const clearSessionStorage = exceptKeys => {
  if (typeof window === 'undefined') {
    // Server-side rendering check
    return;
  }
  try {
    if (!exceptKeys || exceptKeys.length === 0) {
      // Clear everything if no exceptions specified
      sessionStorage.clear();
    } else {
      // Clear all keys except the ones in the exceptKeys array
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && !exceptKeys.includes(key)) {
          keysToRemove.push(key);
        }
      }

      // Remove the keys
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
      });
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        operation: 'sessionStorage_clear'
      }
    });
  }
};